import { ArrowLeft, Building2, Lock, Mail, MapPin, Smartphone, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { setSession } from "@/components/feature/auth/session";
import AuthShell from "./AuthShell";
import { authService } from "@/services/apiServices";

export default function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [shopName, setShopName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = "Please enter your full name.";
    if (!shopName.trim()) newErrors.shopName = "Please enter your shop or business name.";
    if (!email.trim() || !email.includes("@")) newErrors.email = "Please enter a valid email address.";
    if (!mobile.trim() || mobile.replace(/\D/g, "").length !== 10) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number.";
    }
    if (!password || password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        name: name.trim(),
        shopName: shopName.trim(),
        email: email.trim().toLowerCase(),
        mobile: mobile.replace(/\D/g, "").slice(-10),
        password,
        city: city.trim(),
        state: state.trim(),
      };

      const res = await authService.register(payload);
      setLoading(false);

      if (res && res.success && res.data) {
        const { user, token } = res.data;
        setSession({ role: "retailer", retailerId: user.id, token, email: user.email, name: user.name });
        navigate("/retailer");
      } else {
        setErrors({ api: res.message || "Registration failed. Please check your information." });
      }
    } catch (err: any) {
      setLoading(false);
      setErrors({ api: err.message || "Failed to register account. Please try again." });
    }
  }

  return (
    <AuthShell>
      <h2 className="text-2xl font-semibold text-ink">Create Retailer Account</h2>
      <p className="mt-1 text-sm text-ink-secondary">Join MandatePro and start managing e-Mandates</p>

      {errors.api && (
        <div className="mt-4 rounded-md bg-rose-50 p-3 text-sm text-rose-700 border border-rose-200">
          {errors.api}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3.5" noValidate>
        <Input
          label="Full Name *"
          placeholder="e.g. Ramesh Kumar"
          prefixIcon={<User className="h-[18px] w-[18px]" />}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrors((prev) => ({ ...prev, name: "", api: "" }));
          }}
          error={errors.name}
        />

        <Input
          label="Shop / Business Name *"
          placeholder="e.g. Kumar Electronics"
          prefixIcon={<Building2 className="h-[18px] w-[18px]" />}
          value={shopName}
          onChange={(e) => {
            setShopName(e.target.value);
            setErrors((prev) => ({ ...prev, shopName: "", api: "" }));
          }}
          error={errors.shopName}
        />

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Input
            label="Mobile Number *"
            placeholder="10-digit mobile"
            prefixIcon={<Smartphone className="h-[18px] w-[18px]" />}
            value={mobile}
            maxLength={10}
            onChange={(e) => {
              setMobile(e.target.value.replace(/\D/g, ""));
              setErrors((prev) => ({ ...prev, mobile: "", api: "" }));
            }}
            error={errors.mobile}
          />
          <Input
            label="Email Address *"
            type="email"
            placeholder="name@example.com"
            prefixIcon={<Mail className="h-[18px] w-[18px]" />}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: "", api: "" }));
            }}
            error={errors.email}
          />
        </div>

        <Input
          label="Password *"
          type="password"
          placeholder="Minimum 6 characters"
          prefixIcon={<Lock className="h-[18px] w-[18px]" />}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setErrors((prev) => ({ ...prev, password: "", api: "" }));
          }}
          error={errors.password}
        />

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Input
            label="City"
            placeholder="City"
            prefixIcon={<MapPin className="h-[18px] w-[18px]" />}
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <Input
            label="State"
            placeholder="State"
            prefixIcon={<MapPin className="h-[18px] w-[18px]" />}
            value={state}
            onChange={(e) => setState(e.target.value)}
          />
        </div>

        <Button type="submit" fullWidth className="mt-2" loading={loading}>
          Create Account
        </Button>

        <p className="text-center text-sm text-ink-secondary mt-1">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="font-semibold text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            Sign In
          </button>
        </p>
      </form>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-1.5 text-xs text-ink-secondary hover:text-ink cursor-pointer"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to login
        </button>
      </div>
    </AuthShell>
  );
}
